import { useState, useCallback, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import { getDocs, collection } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';
import { db } from '@/firebase/config';
import { useQuote } from '../hooks/useQuote';
import { useAuth } from '@modules/auth';
import { fetchCustomers, createCustomer } from '@modules/customer';
import { useEmployee } from '@modules/master/hooks/useEmployee';
import { fetchTemplates } from '@modules/document';
import { createOrder, updateOrder, fetchOrder } from '@modules/order';
import { fetchQuote, fetchQuoteItems, nextOrderQuoteBranch } from '../services/quote.service';
import type { QuoteCreateInput, QuoteItemInput } from '../services/quote.service';
import { findIncompleteLineItemIndex, incompleteLineItemMessage } from '@shared/utils';
import type { Customer, CustomerContact, DocumentTemplate, PartnerSnapshot } from '@shared/types';

export interface FromOrderState {
  orderId: string;
  orderNo: string;
  customerId: string;
  title: string;
  assignedUserId: string;
}

export interface NewCustomerInput {
  name: string;
  nameKana: string;
  phone: string;
  fax: string;
  postalCode: string;
  addr1: string;
  addr2: string;
  addr3: string;
  tel2: string;
  code: string;
}

const EMPTY_NEW_CUSTOMER: NewCustomerInput = {
  name: '', nameKana: '', phone: '', fax: '',
  postalCode: '', addr1: '', addr2: '', addr3: '',
  tel2: '', code: '',
};

// 見積書テーブルのデフォルト表示行数（新規・編集とも最低この行数を空行で埋める）
const MIN_ITEM_ROWS = 15;

const blankItem = (sortOrder: number): QuoteItemInput => ({
  productId: null, name: '', description: null,
  quantity: 0, unit: '', unitPrice: 0,
  discountRate: 0, taxType: 'standard', sortOrder,
});

/** items を最低 MIN_ITEM_ROWS 行まで空行でパディングする。 */
const padItems = (items: QuoteItemInput[]): QuoteItemInput[] => {
  if (items.length >= MIN_ITEM_ROWS) return items;
  const fillers = Array.from({ length: MIN_ITEM_ROWS - items.length }, (_, i) =>
    blankItem(items.length + i),
  );
  return [...items, ...fillers];
};

/**
 * 見積有効期限の既定値: 見積日（当日）から1ヶ月後。
 * 見積日は当日固定のため、フォーム表示時点を基準に都度計算する（ユーザーは手動変更可）。
 */
function defaultValidUntil(): Timestamp {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return Timestamp.fromDate(d);
}

const DEFAULT_FORM: QuoteCreateInput = {
  customerId: '', contactId: null, orderId: null, title: '',
  validUntil: defaultValidUntil(),
  notes: null, internalNotes: null,
  assignedUserId: '', templateId: null,
  taxCalcMode: 'floor', advance: 0, totalgst: 0, grandtotal: 0,
  disabled: false, code: null, samemaster: false,
  chiefEmployerId: null, atenaType: 'company_honorific', paymentTerm: '',
  businessTypeId: null, usergroupId: null,
  stampFlags: { tantou: false, kakunin: false, kaisha: true },
};

interface UseQuoteFormOptions {
  id?: string;
  onSaved: (quoteId: string) => void;
  /** 埋め込み表示時に location.state の代わりに使う初期化コンテキスト。 */
  state?: unknown;
}

export function useQuoteForm({ id, onSaved, state }: UseQuoteFormOptions) {
  const location = useLocation();
  // 埋め込み表示時は呼び出し側から渡された state を優先する（ルーター state は受注詳細のもの）
  const navState = state !== undefined ? state : location.state;
  const fromOrderState = (navState as { fromOrder?: FromOrderState } | null)?.fromOrder ?? null;
  // 編集時は受注引き継ぎUI（バナー・見積先トグル）を表示しない。
  // 保存/キャンセル後の遷移は QuoteFormPage 側が location.state から直接判定する。
  const fromOrder = id ? null : fromOrderState;

  const INIT_ITEMS: QuoteItemInput[] = Array.from({ length: MIN_ITEM_ROWS }, (_, i) => blankItem(i));

  // 見積有効期限の既定は「見積日（当日）から1ヶ月後」。フォーム表示時点で都度算出する（手動変更可）。
  const initialForm: QuoteCreateInput = fromOrder
    ? { ...DEFAULT_FORM, validUntil: defaultValidUntil(), orderId: fromOrder.orderId, customerId: fromOrder.customerId, title: fromOrder.title }
    : { ...DEFAULT_FORM, validUntil: defaultValidUntil() };

  const { user } = useAuth();
  const { addQuote, editQuote, upsertItems } = useQuote();
  const [form, setForm] = useState<QuoteCreateInput>(initialForm);
  const [items, setItems] = useState<QuoteItemInput[]>(INIT_ITEMS);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  // 受注継承時の依頼元スナップショット（受注作成時点のフル情報。見積先の継承表示に使用）
  const [orderSnapshot, setOrderSnapshot] = useState<PartnerSnapshot | null>(null);
  // 受注引き継ぎモード: true=顧客継承, false=継承解除
  const [inheritMode, setInheritMode] = useState<boolean>(Boolean(fromOrder));
  // 編集時の見積先継承トグル用の継承元（読込済み見積の受注・顧客）
  const [editInherit, setEditInherit] = useState<{ orderId: string | null; customerId: string } | null>(null);
  // 新規顧客入力
  const [newCustomer, setNewCustomer] = useState<NewCustomerInput>(EMPTY_NEW_CUSTOMER);
  const [, setDupCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 編集対象が「AIレビュー待ち」か（form は QuoteCreateInput のためレコードの印は別stateで保持）
  const [aiPending, setAiPending] = useState(false);
  const { employees, loadEmployees } = useEmployee();

  useEffect(() => {
    void Promise.all([
      fetchCustomers().then((list) => {
        setCustomers(list);
        // 受注から引き継いだ顧客IDがあれば selectedCustomer を設定
        if (fromOrder?.customerId) {
          const found = list.find((c) => c.id === fromOrder.customerId) ?? null;
          setSelectedCustomer(found);
        }
      }),
      fetchTemplates('quote').then(setTemplates).catch(() => setTemplates([])),
      loadEmployees(),
    ]).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : '初期データの取得に失敗しました');
    });
  }, [loadEmployees, fromOrder?.customerId]);

  // form.customerId に追従して selectedCustomer を設定する。
  // 編集画面の読込・受注継承・顧客選択のいずれでも見積先の詳細（電話・住所・締日等）を表示するため。
  useEffect(() => {
    if (!form.customerId) { setSelectedCustomer(null); return; }
    const found = customers.find((c) => c.id === form.customerId);
    if (found) setSelectedCustomer(found);
  }, [form.customerId, customers]);

  // 受注から継承した場合、受注の依頼元スナップショット（フル情報）を読み込み見積先に表示し、
  // 受注で選択済みの業種・担当部署も見積ヘッダーへ自動継承する。
  useEffect(() => {
    if (!fromOrder?.orderId) { setOrderSnapshot(null); return; }
    let active = true;
    void fetchOrder(fromOrder.orderId)
      .then((o) => {
        if (!active) return;
        setOrderSnapshot(o?.customerSnapshot ?? null);
        // 受注で業種・担当部署が選択されていれば見積に引き継ぐ（未選択なら現状維持）
        if (o) {
          setForm((f) => ({
            ...f,
            businessTypeId: o.businessTypeId ?? f.businessTypeId,
            usergroupId: o.usergroupId ?? f.usergroupId,
          }));
        }
      })
      .catch(() => { if (active) setOrderSnapshot(null); });
    return () => { active = false; };
  }, [fromOrder?.orderId]);

  const upd = useCallback((patch: Partial<QuoteCreateInput>) => {
    setForm((f) => ({ ...f, ...patch }));
  }, []);

  const loadContacts = useCallback(async (customerId: string) => {
    try {
      const snap = await getDocs(collection(db, 'customers', customerId, 'contacts'));
      setContacts(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as CustomerContact));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '連絡先の取得に失敗しました。');
      setContacts([]);
    }
  }, []);

  const handleCustomerChange = useCallback((customerId: string) => {
    upd({ customerId, contactId: null });
    const found = customers.find((c) => c.id === customerId) ?? null;
    setSelectedCustomer(found);
    if (customerId) void loadContacts(customerId);
    else setContacts([]);
  }, [customers, upd, loadContacts]);

  const loadEdit = useCallback(async (quoteId: string) => {
    setLoading(true);
    try {
      const [q, qItems] = await Promise.all([fetchQuote(quoteId), fetchQuoteItems(quoteId)]);
      if (!q) { setError('見積が見つかりません'); return; }
      setAiPending(q.aiPendingReview === true);
      setForm({
        customerId: q.customerId, contactId: q.contactId, orderId: q.orderId ?? null, title: q.title,
        validUntil: q.validUntil, notes: q.notes, internalNotes: q.internalNotes,
        assignedUserId: q.assignedUserId, templateId: q.templateId,
        taxCalcMode: q.taxCalcMode ?? 'floor',
        advance: q.advance ?? 0, totalgst: q.totalgst ?? 0, grandtotal: q.grandtotal ?? 0,
        disabled: q.disabled ?? false, code: q.code ?? null,
        samemaster: q.samemaster ?? false, chiefEmployerId: q.chiefEmployerId ?? null,
        atenaType: q.atenaType ?? 'company_honorific', paymentTerm: q.paymentTerm ?? '',
        businessTypeId: q.businessTypeId ?? null, usergroupId: q.usergroupId ?? null,
        stampFlags: q.stampFlags ?? { tantou: false, kakunin: false, kaisha: false },
      });
      setItems(padItems(qItems.map((it) => ({
        productId: it.productId, name: it.name, description: it.description,
        quantity: it.quantity, unit: it.unit, unitPrice: it.unitPrice,
        discountRate: it.discountRate, taxType: it.taxType, sortOrder: it.sortOrder,
      }))));
      await loadContacts(q.customerId);
      // 編集時も「顧客継承／継承解除」トグルを有効化し、初期は継承状態（全欄表示）にする
      setEditInherit({ orderId: q.orderId ?? null, customerId: q.customerId });
      setInheritMode(Boolean(q.customerId));
      // 紐づく受注の依頼元スナップショット（フル情報）を見積先の継承表示に使う
      if (q.orderId) {
        try {
          const o = await fetchOrder(q.orderId);
          setOrderSnapshot(o?.customerSnapshot ?? null);
        } catch { setOrderSnapshot(null); }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '見積の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [loadContacts]);

  const handleSubmit = useCallback(async () => {
    if (!form.title) { setError('件名は必須です'); return; }
    if (!form.assignedUserId) { setError('担当者を選択してください'); return; }
    setSaving(true); setError(null);
    try {
      let customerId = form.customerId;

      // 新規顧客モード（受注引き継ぎなし）の場合、先に顧客を登録
      if (!inheritMode && !id && newCustomer.name) {
        if (!newCustomer.nameKana) { setError('フリガナは必須です'); setSaving(false); return; }
        if (!user) { setError('ログインが必要です'); setSaving(false); return; }
        const created = await createCustomer({
          name: newCustomer.name,
          nameKana: newCustomer.nameKana,
          partnerTypes: ['customer'],
          rank: 'B',
          rankMode: 'auto',
          status: 'prospect',
          industry: '',
          postalCode: newCustomer.postalCode || '0000000',
          prefecture: '',
          phone: newCustomer.phone || '',
          fax: newCustomer.fax || null,
          email: null,
          website: null,
          assignedUserId: null,
          paymentTerms: 30,
          notes: null,
          addr1: newCustomer.addr1,
          addr2: newCustomer.addr2,
          addr3: newCustomer.addr3,
          department: '',
          tel2: newCustomer.tel2 || null,
          group: null,
          parentId: null,
          areaId: null,
          limitTypeId: null,
          paymentDateId: null,
          taxCalcMode: 'floor',
        }, user.id);
        customerId = created.id;
      }

      if (!customerId) { setError('顧客を入力してください'); setSaving(false); return; }

      const finalForm = { ...form, customerId };

      // 空行判定: 何も入力されていない行（品名・数量・単位・単価すべて未入力）を空行とみなす。
      const isBlank = (it: QuoteItemInput) =>
        it.name.trim() === '' && it.quantity === 0 && it.unit.trim() === '' && it.unitPrice === 0;

      // 末尾の空行のみ除去し、途中の空行は帳票レイアウト用に保持する（行を詰めない）
      let lastFilled = -1;
      for (let i = items.length - 1; i >= 0; i--) {
        if (!isBlank(items[i])) { lastFilled = i; break; }
      }
      if (lastFilled < 0) {
        setError('明細を1行以上入力してください');
        setSaving(false);
        return;
      }
      const keptItems = items.slice(0, lastFilled + 1);

      // 数量・単位・単価のいずれかが入力された行のみ、品目名・数量(1以上)・単位の必須チェックを行う。
      // 品名（摘要）のみのフリー記載行は許可する（全帳票フォーム共通仕様）。
      const invalidIndex = findIncompleteLineItemIndex(keptItems);
      if (invalidIndex >= 0) {
        setError(incompleteLineItemMessage(invalidIndex));
        setSaving(false);
        return;
      }

      const numbered = keptItems.map((it, i) => ({ ...it, sortOrder: i }));
      if (id) {
        await editQuote(id, finalForm);
        await upsertItems(id, numbered);
        onSaved(id);
      } else {
        if (!user) { setError('ログインが必要です'); setSaving(false); return; }
        // 見積は必ず受注（案件）に紐づく。受注から作成した場合はその案件を使い、
        // 単独作成の場合は案件を自動生成して紐づける（Mr.販管「1案件=1受注番号」仕様）。
        let orderId = fromOrder?.orderId ?? null;
        let orderNo = fromOrder?.orderNo;
        if (!orderId) {
          const order = await createOrder(
            {
              customerId, title: finalForm.title, assignedUserId: finalForm.assignedUserId,
              contactId: null, quoteId: null, deliveryDate: null, notes: null,
              businessTypeId: null, usergroupId: null, taxCalcMode: 'floor',
            },
            user.id,
          );
          orderId = order.id;
          orderNo = order.orderNo;
        }
        // 案件内の見積は「受注番号-連番」の単純追番（1本目から 0001-1）。
        const code = await nextOrderQuoteBranch(orderId, orderNo);
        const quote = await addQuote({ ...finalForm, orderId, code });
        await upsertItems(quote.id, numbered);
        // 案件側にも紐付ける（案件詳細の見積書管理タブに表示するため）
        await updateOrder(orderId, { quoteId: quote.id }, user.id);
        onSaved(quote.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  }, [form, items, id, inheritMode, newCustomer, user, fromOrder, addQuote, editQuote, upsertItems, onSaved]);

  // 「受注先より」→「新規に入力する」へ切り替え
  const switchToNew = useCallback(() => {
    setInheritMode(false);
    setNewCustomer(EMPTY_NEW_CUSTOMER);
    setDupCount(0);
    setForm((f) => ({ ...f, customerId: '', contactId: null }));
    setSelectedCustomer(null);
    setContacts([]);
  }, []);

  // 「継承解除」→「顧客継承」へ切り替え（作成時は受注、編集時は読込済み見積を継承元とする）
  const switchToInherit = useCallback(() => {
    const cid = fromOrder?.customerId ?? editInherit?.customerId;
    if (!cid) return;
    setInheritMode(true);
    setDupCount(0);
    const found = customers.find((c) => c.id === cid) ?? null;
    setSelectedCustomer(found);
    setForm((f) => ({ ...f, customerId: cid, contactId: null }));
    void loadContacts(cid);
  }, [fromOrder, editInherit, customers, loadContacts]);

  return {
    form, items, customers, employees, templates,
    contacts, selectedCustomer, orderSnapshot,
    fromOrder, inheritMode, switchToNew, switchToInherit,
    // 継承トグルの表示可否（受注からの作成 or 受注に紐づく見積の編集）
    inheritable: Boolean(fromOrder) || Boolean(editInherit),
    loading, saving, error,
    aiPending, setAiPending,
    upd, setItems, handleCustomerChange,
    loadEdit, handleSubmit,
  };
}
